import { useState } from "react";
import { toast } from "sonner";
import API from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { X, Plus } from "@phosphor-icons/react";
import { Section, err } from "./hrUtils";

/** HR settings: departments, default probation length and reminder windows. */
export function HrSettingsTab({ meta, onSaved }) {
  const [deps, setDeps] = useState(meta.departments || []);
  const [newDep, setNewDep] = useState("");
  const [probation, setProbation] = useState(meta.settings?.probation_months ?? 6);
  const [rtwDays, setRtwDays] = useState(meta.settings?.rtw_reminder_days ?? 60);
  const [docDays, setDocDays] = useState(meta.settings?.doc_reminder_days ?? 60);
  const [appraisal, setAppraisal] = useState(meta.settings?.appraisal_cycle_months ?? 12);
  const [bfWatch, setBfWatch] = useState(meta.settings?.bradford_watch_score ?? 51);
  const [bfAction, setBfAction] = useState(meta.settings?.bradford_action_score ?? 201);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const r = await API.put("/hr/settings", { departments: deps, probation_months: probation, rtw_reminder_days: rtwDays, doc_reminder_days: docDays, appraisal_cycle_months: appraisal, bradford_watch_score: bfWatch, bradford_action_score: bfAction });
      toast.success(r.data.message);
      onSaved?.();
    } catch (e) { err(e, "Couldn't save HR settings"); } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="hr-settings">
      <Section title="Departments" testid="hr-settings-departments">
        <div className="flex flex-wrap gap-2 mb-3">
          {deps.map((d) => (
            <span key={d} className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs" data-testid={`hr-dep-${d}`}>{d}<button type="button" onClick={() => setDeps(deps.filter((x) => x !== d))} aria-label={`Remove ${d}`} data-testid={`hr-dep-remove-${d}`}><X size={12} /></button></span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input value={newDep} onChange={(e) => setNewDep(e.target.value)} placeholder="Add a department" className="h-8" onKeyDown={(e) => { if (e.key === "Enter" && newDep.trim()) { setDeps([...new Set([...deps, newDep.trim()])]); setNewDep(""); } }} data-testid="hr-dep-input" />
          <Button size="sm" variant="outline" onClick={() => { if (newDep.trim()) { setDeps([...new Set([...deps, newDep.trim()])]); setNewDep(""); } }} data-testid="hr-dep-add"><Plus size={14} /></Button>
        </div>
      </Section>
      <Section title="Defaults & reminders" testid="hr-settings-defaults">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div><Label className="text-xs">Probation (months)</Label><Input type="number" min={0} max={24} value={probation} onChange={(e) => setProbation(e.target.value)} className="mt-1" data-testid="hr-set-probation" /><p className="text-[11px] text-muted-foreground mt-1">Filled in from the start date on new records.</p></div>
          <div><Label className="text-xs">Right-to-work warning (days)</Label><Input type="number" min={7} max={365} value={rtwDays} onChange={(e) => setRtwDays(e.target.value)} className="mt-1" data-testid="hr-set-rtw" /></div>
          <div><Label className="text-xs">Document expiry warning (days)</Label><Input type="number" min={7} max={365} value={docDays} onChange={(e) => setDocDays(e.target.value)} className="mt-1" data-testid="hr-set-doc" /></div>
          <div><Label className="text-xs">Appraisal cycle (months)</Label><Input type="number" min={3} max={36} value={appraisal} onChange={(e) => setAppraisal(e.target.value)} className="mt-1" data-testid="hr-set-appraisal" /></div>
          <div><Label className="text-xs">Bradford factor — watch from</Label><Input type="number" min={1} max={5000} value={bfWatch} onChange={(e) => setBfWatch(e.target.value)} className="mt-1" data-testid="hr-set-bradford-watch" /></div>
          <div><Label className="text-xs">Bradford factor — formal review from</Label><Input type="number" min={1} max={10000} value={bfAction} onChange={(e) => setBfAction(e.target.value)} className="mt-1" data-testid="hr-set-bradford-action" /></div>
        </div>
        <div className="mt-4 flex justify-end"><Button onClick={save} disabled={busy} data-testid="hr-settings-save">{busy ? "Saving…" : "Save settings"}</Button></div>
      </Section>
    </div>
  );
}
